import { InteractiveQuestionResponseMessage } from "#components/ui/interactive-question/InteractiveQuestionResponseMessage.tsx";
import { parseInteractiveQuestionResponse } from "#components/ui/interactive-question/interactiveQuestionResponse.ts";
import { MentionToken } from "#features/conversations/components/conversation-input/MentionToken.tsx";
import { SkillToken } from "#features/conversations/components/conversation-input/SkillToken.tsx";
import type { SkillDisplay } from "#features/conversations/hooks/useSkillDisplays.ts";
import { useWlAppLinks } from "#features/conversations/hooks/useWlAppLinks.ts";
import type { MemberDisplay } from "#features/conversations/stores/useMemberDisplayStore.ts";
import { CODE_BLOCK_NODE, codeInfo, type MarkupPart, MENTION_NODE, parseMarkup, QUOTE_NODE, SKILL_NODE } from "#features/conversations/utils/composerDocument.ts";
import type { ConversationMessage } from "#types/types.ts";
import { type ColorValue, Div, Flex, useColorSchemeValue } from "@hopper-ui/components";
import { memo, useMemo } from "react";
import "./UserMessage.css";
import "./UserMessage.dark.css";

// What a line holds: text, tokens and inline code.
type LinePart = Exclude<MarkupPart, { type: typeof CODE_BLOCK_NODE | typeof QUOTE_NODE }>;

interface Props {
    message: ConversationMessage;
    // The skills a "/slug" can name, by slug; resolved once for the whole conversation.
    skillDisplays: ReadonlyMap<string, SkillDisplay>;
    // The photos and job titles of the members a mention can name, by id; resolved once for the whole conversation.
    memberDisplays: ReadonlyMap<string, MemberDisplay>;
}

const UserMessageContent = ({ message, skillDisplays, memberDisplays }: Props) => {
    const { linksToProfile, skillPageHref } = useWlAppLinks();
    const interactiveQuestionAnswers = parseInteractiveQuestionResponse(message.content);
    const parts = useMemo(() => parseMarkup(message.content, skillDisplays), [message.content, skillDisplays]);

    const textColor = useColorSchemeValue(
        "neutral-strong",
        "core_orchid-bloom-75"
    ) as ColorValue;

    const backgroundColor = useColorSchemeValue(
        "var(--hop-orchid-bloom-600)",
        "linear-gradient(92deg, #5454be 0%, #322b8d 100%)"
    );

    const renderLinePart = (part: LinePart) => {
        if (typeof part === "string") {
            return part;
        }
        if (part.type === SKILL_NODE) {
            const display = skillDisplays.get(part.slug);

            return <SkillToken key={part.start} title={display?.title ?? `/${part.slug}`} writtenAs={`/${part.slug}`} card={display && { description: display.description, isPressable: true, pageHref: skillPageHref(part.slug) }} />;
        }
        if (part.type === MENTION_NODE) {
            const member = memberDisplays.get(part.id);

            return <MentionToken key={part.start} name={part.label} imageUrl={member?.imageUrl} card={{ memberId: part.id, jobTitle: member?.jobTitle, isPressable: true, linksToProfile }} copiesWithAt />;
        }

        return <code key={part.start} className="wai-user-message-code">{part.text}</code>;
    };

    if (interactiveQuestionAnswers) {
        return <InteractiveQuestionResponseMessage answers={interactiveQuestionAnswers} />;
    }

    return (
        <Flex justifyContent="end" className="wai-user-message">
            <Div
                whiteSpace="pre-wrap"
                borderRadius="pill"
                paddingY="core_80"
                paddingX="inset-md"
                wordBreak="break-word"
                fontSize="body-sm"
                marginLeft="core_320"
                UNSAFE_lineHeight="var(--wai-prose-line-height)"
                color={textColor}
                style={{ background: backgroundColor }}
                className="wai-user-message-content"
            >
                {parts.map(part => {
                    if (typeof part === "string") {
                        return part;
                    }
                    if (part.type === CODE_BLOCK_NODE) {
                        return <pre key={part.start} className="wai-user-message-code-block" data-info={codeInfo(part.opening) || undefined}><code>{part.codeLines.join("\n")}</code></pre>;
                    }
                    if (part.type === QUOTE_NODE) {
                        return (
                            <blockquote key={part.start} className="wai-user-message-quote">
                                {part.lines.flatMap((line, index) => [...(index > 0 ? ["\n"] : []), ...line.parts.map(renderLinePart)])}
                            </blockquote>
                        );
                    }

                    return renderLinePart(part);
                })}
            </Div>
        </Flex>
    );
};

// A message renders again only when it or what it reads changes, not for every streamed chunk of the reply after it.
export const UserMessage = memo(UserMessageContent);
